import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlacements } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';
import List from 'core/modules/Partials/List/List';

import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import FeaturedOffer120x90 from './components/FeaturedOffer120x90/FeaturedOffer120x90';
import {
  selectFeaturedOffers120x90Config, makeSelectFeaturedOffers120x90Data,
  makeSelectFeaturedOffers120x90IsLoaded, makeSelectFeaturedOffers120x90IsLoading,
} from './selectors';

import './FeaturedOffers120x90.scss';

function FeaturedOffers120x90({
  namespace = 'default', section_title: sectionTitle, max_items: limit, content_group_id,
}) {
  const selectFeaturedOffers120x90Data = makeSelectFeaturedOffers120x90Data();
  const selectFeaturedOffers120x90IsLoading = makeSelectFeaturedOffers120x90IsLoading();
  const selectFeaturedOffers120x90IsLoaded = makeSelectFeaturedOffers120x90IsLoaded();

  const config = useSelector(selectFeaturedOffers120x90Config);
  const isLoading = useSelector(state => selectFeaturedOffers120x90IsLoading(state, namespace));
  const isLoaded = useSelector(state => selectFeaturedOffers120x90IsLoaded(state, namespace));
  const data = useSelector(state => selectFeaturedOffers120x90Data(state, namespace));
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const shouldUseCarousel = isMobileMQ && data && data.length > 1;

  const {
    carousel,
    api: { options: { sectionId } },
  } = config;

  const dispatch = useDispatch();

  const [visibleItemsCount, setVisibleItemsCount] = useState(null);

  const updateItemsCount = () => {
    if (window.innerWidth >= 1060) {
      setVisibleItemsCount(6);
    } else if (window.innerWidth >= 890) {
      setVisibleItemsCount(5);
    } else if (window.innerWidth >= 768) {
      setVisibleItemsCount(4);
    } else {
      // default for smaller screens
      setVisibleItemsCount(limit);
    }
  };

  useEffect(() => {
    if (!shouldUseCarousel) {
      updateItemsCount();
      window.addEventListener('resize', updateItemsCount);
    }
    return () => {
      window.removeEventListener('resize', updateItemsCount);
    };
  }, []);

  const dataToDisplay = data.slice(0, visibleItemsCount);

  const getFeaturedOfferContent = (sectionId) => {
    const { useAddToFavorite } = config;

    return (data && dataToDisplay.map(dataItem => (
      <FeaturedOffer120x90
        key={dataItem.id}
        dataItem={dataItem}
        sectionId={sectionId}
        useAddToFavorite={useAddToFavorite}
      />
    )));
  };

  const callFetchPlacements = () => {
    const { api: { params, options } } = config;
    const paramsWithContentGroup = {
      content_group_id,
      limit,
      ...params,
    };

    if (!paramsWithContentGroup.content_group_id) {
      return false; // content_group_id param is required for /placements API endpoint
    }

    dispatch(fetchPlacements(paramsWithContentGroup, { ...options, namespace }));
    return true;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      callFetchPlacements();
    }
  }, []);

  if (isEmpty(data)) {
    return null;
  }

  return (
    <div
      className="mn_featuredOffers120x90"
      data-test="featured-offers-120x90"
      data-title={sectionTitle}
    >
      <SectionTitle title={sectionTitle} />
      <List options={carousel} shouldUseCarousel={shouldUseCarousel}>
        {getFeaturedOfferContent(sectionId)}
      </List>
    </div>
  );
}

export default (props) =>
  <ComponentErrorBoundary><FeaturedOffers120x90 {...props} /></ComponentErrorBoundary>;
